import { useEffect, useMemo, useState } from 'react';
import { Card, Segmented, DatePicker, Select, Typography, Row, Col, Tag, Alert, Switch } from 'antd';
import { CalendarOutlined, BankOutlined, PartitionOutlined } from '@ant-design/icons';
import axiosInstance from '../../../../variables/api/axiosInstance';
import SideBar from '../../../../components/SideBar';
import NavBar from '../../../../components/NavBar';
import Footer from '../../../../components/Footer';
import PoolGumVtEtat from './PoolGumVtEtat';
import SapRepartiEtat from './SapRepartiEtat';

const { Text, Title } = Typography;
const PRIMARY = '#242166';

// Accent couleur par mode d'affichage.
const MODE_COLOR = { POOL: '#242166', GUM: '#7c3aed', VT: '#2563eb', TPV: '#0891b2', MOTO: '#c2410c' };

// Le mode « POOL » = état croisé combiné GUM + VT (celui qui existe déjà, PoolGumVtEtat sans branche).
// GUM / VT = mêmes composant/état, filtré sur une branche. TPV / MOTO = répartition généralisée (v2).
const MODES = [
    { value: 'POOL', label: 'Pool (GUM & VT)' },
    { value: 'GUM', label: 'GUM' },
    { value: 'VT', label: 'VT' },
    { value: 'TPV', label: 'TPV' },
    { value: 'MOTO', label: 'MOTO' },
];
// Modes servis par l'état Pool existant (PoolGumVtEtat) : on garde sa présentation à l'identique.
const POOL_MODES = new Set(['POOL', 'GUM', 'VT']);

const StatistiquesSAPV2 = () => {
    const [mode, setMode] = useState('POOL');       // défaut = état Pool GUM+VT combiné
    const [orgCode, setOrgCode] = useState(null);   // null = Pool / toutes compagnies ; sinon focus compagnie
    const [arreteAu, setArreteAu] = useState(null); // null = SAP COURANT (persisté), cf. leçon v1
    const [inclureOgar, setInclureOgar] = useState(false); // ALLIANZ toujours exclu ; OGAR optionnel (défaut exclu)
    const [organismes, setOrganismes] = useState([]);

    useEffect(() => {
        axiosInstance.get('/api/organisme/all/assurances-pool')
            .then((res) => setOrganismes(Array.isArray(res.data) ? res.data : []))
            .catch((err) => console.error('Erreur organismes', err));
    }, []);

    // L'organisme sélectionné est-il le Pool ? (code '00' ou type POOL) → pas de focus compagnie.
    const orgSel = useMemo(() => organismes.find((o) => o.code === orgCode), [organismes, orgCode]);
    const isPoolOrg = !orgCode || (orgSel && (orgSel.typeOrganisme === 'POOL' || orgSel.code === '00'));
    // Compagnie mise en avant dans les états répartis (TPV/MOTO) : uniquement si une vraie compagnie est choisie.
    const focusCompagnie = isPoolOrg ? null : orgCode;

    const accent = MODE_COLOR[mode] || PRIMARY;
    const isPoolEtat = POOL_MODES.has(mode);
    // Pour PoolGumVtEtat : POOL → null (les deux), sinon la branche.
    const poolBranche = mode === 'POOL' ? null : mode;

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper" style={{ background: '#f0f2f5' }}>
                        <div className="container-xxl flex-grow-1 container-p-y">

                            {/* En-tête */}
                            <div style={{ background: `linear-gradient(135deg, ${accent} 0%, ${PRIMARY} 100%)`, borderRadius: 16, padding: '20px 24px', marginBottom: 16, color: '#fff' }}>
                                <Title level={3} style={{ color: '#fff', margin: 0 }}>
                                    <PartitionOutlined /> État des SAP — répartition par compagnie (v2)
                                </Title>
                                <Text style={{ color: 'rgba(255,255,255,0.85)' }}>
                                    {mode === 'POOL'
                                        ? "État du Pool inchangé : SAP GUM & VT croisé par compagnie."
                                        : `SAP ${mode} réparti entre les compagnies suivant la clé de répartition.`}
                                </Text>
                            </div>

                            {/* Barre de sélection */}
                            <Card bordered={false} style={{ borderRadius: 12, marginBottom: 14 }} bodyStyle={{ padding: 12 }}>
                                <Row gutter={[12, 12]} align="middle">
                                    <Col xs={24} md={10}>
                                        <div style={{ marginBottom: 4 }}>
                                            <Text type="secondary"><BankOutlined /> Compagnie / organisme</Text>
                                        </div>
                                        <Select
                                            style={{ width: '100%' }}
                                            value={orgCode}
                                            onChange={(v) => setOrgCode(v || null)}
                                            allowClear
                                            showSearch
                                            optionFilterProp="label"
                                            placeholder="Pool — toutes les compagnies"
                                            options={organismes.map((o) => ({ value: o.code, label: o.raisonSociale || o.nom || o.code }))}
                                        />
                                    </Col>
                                    <Col xs={24} md={9}>
                                        <div style={{ marginBottom: 4 }}>
                                            <Text type="secondary"><PartitionOutlined /> Branche</Text>
                                        </div>
                                        <Segmented value={mode} onChange={setMode} options={MODES} size="large" block />
                                    </Col>
                                    <Col xs={24} md={5}>
                                        <div style={{ marginBottom: 4 }}>
                                            <Text type="secondary"><CalendarOutlined /> Arrêté au (vide = courant)</Text>
                                        </div>
                                        <DatePicker style={{ width: '100%' }} value={arreteAu} onChange={(d) => setArreteAu(d || null)}
                                            format="DD/MM/YYYY" placeholder="Aujourd'hui (courant)" allowClear />
                                    </Col>
                                </Row>
                                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <Switch checked={inclureOgar} onChange={setInclureOgar} size="small" />
                                    <Text type="secondary">Inclure les sinistres d'OGAR ASSURANCES</Text>
                                    <Tag color="default" style={{ marginLeft: 8 }}>ALLIANZ toujours exclu</Tag>
                                </div>
                                {focusCompagnie && (
                                    <div style={{ marginTop: 10 }}>
                                        {isPoolEtat ? (
                                            <Alert type="info" showIcon banner
                                                message={`L'état ${mode === 'POOL' ? 'GUM & VT' : mode} du Pool présente toutes les compagnies. Choisir TPV ou MOTO pour mettre en avant ${orgSel?.raisonSociale || focusCompagnie}.`} />
                                        ) : (
                                            <Tag color="orange">Compagnie mise en avant : {orgSel?.raisonSociale || focusCompagnie}</Tag>
                                        )}
                                    </div>
                                )}
                            </Card>

                            {/* Contenu : état Pool (GUM/VT combiné ou filtré) ou état réparti (TPV/MOTO) */}
                            {isPoolEtat ? (
                                <PoolGumVtEtat arreteAu={arreteAu} branche={poolBranche} inclureOgar={inclureOgar} />
                            ) : (
                                <Card bordered={false} style={{ borderRadius: 12 }}>
                                    <SapRepartiEtat branche={mode} arreteAu={arreteAu} highlightCompagnie={focusCompagnie} inclureOgar={inclureOgar} />
                                </Card>
                            )}
                        </div>
                        <Footer />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StatistiquesSAPV2;
